// Klub panel — közös elemek: foglalás-részletek oldalpanel (OP_03b) és új foglalás rögzítése.
import React, { useMemo, useState } from 'react';
import { addDoc, collection, doc, DocumentData, serverTimestamp, Timestamp, updateDoc, UpdateData } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Icon } from '../icons';
import { fmtInt } from '../data';
import { Avatar, Badge, Button, ConfirmModal, Field, Input, Modal, Select, Toggle, Tone, useToast } from '../ui';
import { bookingPrice, ClubBoard, ClubBooking, ClubInfo, openWindow, usePeople } from './clubData';

export const KIND_LABEL: Record<ClubBooking['kind'], string> = { cp: 'CP rangsorolt', friendly: 'Baráti meccs', manual: 'Sima foglalás', event: 'Esemény' };
export const KIND_TONE: Record<ClubBooking['kind'], Tone> = { cp: 'cyan', friendly: 'green', manual: 'green', event: 'purple' };

export function statusOf(b: ClubBooking): { t: string; tone: Tone } {
  if (b.state === 'cancelled') return { t: 'Lemondva', tone: 'red' };
  if (b.arrived) return { t: 'Megérkezett', tone: 'green' };
  if (b.state === 'pending') return { t: 'Függőben', tone: 'gold' };
  if (b.paid) return { t: 'Fizetve', tone: 'cyan' };
  return { t: 'Visszaigazolva', tone: 'cyan' };
}

export const hhmm = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
export const longDate = (d: Date) => d.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' });

const POLICY: Record<string, string> = { laza: 'Laza', kozepes: 'Közepes', szigoru: 'Szigorú' };

/* ---------- Foglalás részletei (oldalpanel) ---------- */
export function BookingDrawer({ b, boards, club, onClose }: { b: ClubBooking; boards: ClubBoard[]; club: ClubInfo; onClose: () => void }) {
  const toast = useToast();
  const who = usePeople([b.createdBy, ...b.participantIds]);
  const [moving, setMoving] = useState(false);
  const [cancel, setCancel] = useState(false);
  const st = statusOf(b);
  const price = bookingPrice(b, boards);
  const people = Array.from(new Set([b.createdBy, ...b.participantIds])).filter(Boolean);
  const upd = async (patch: UpdateData<DocumentData>, ok: string) => {
    try { await updateDoc(doc(db, 'bookings', b.id), patch); toast(ok); } catch { toast('A módosítás nem sikerült.', 'red'); }
  };

  return (
    <>
      <div className="fixed inset-0 z-[58] bg-black/40" onClick={onClose} />
      <aside className="fixed right-0 top-0 z-[59] h-screen w-full max-w-[400px] bg-[#15191E] border-l border-[#31383D] p-6 overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[15px] font-extrabold italic uppercase text-white">Foglalás részletei</h3>
          <button type="button" onClick={onClose} className="text-[#B8B8C3] hover:text-white" aria-label="Bezárás"><Icon name="X" size={22} /></button>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Badge tone={KIND_TONE[b.kind]}>{KIND_LABEL[b.kind]}</Badge>
          <Badge tone={st.tone}>{st.t}</Badge>
        </div>
        <div className="mt-3 text-[22px] font-bold text-white">{hhmm(b.start)} – {hhmm(b.end)} · {b.board}. tábla</div>
        <div className="mt-1 text-[12px] text-[#8a8f99]">{longDate(b.start)} · {b.durationMin} perc</div>

        <div className="mt-5 pt-4 border-t border-[#31383D]">
          <h4 className="text-[13px] font-extrabold italic uppercase text-white mb-3">Játékosok ({b.guestName ? 1 : people.length})</h4>
          {b.guestName ? (
            <div className="flex items-center gap-3"><Avatar name={b.guestName} size={34} /><div><div className="text-[13px] font-semibold text-white">{b.guestName}</div><div className="text-[11px] text-[#8a8f99]">Vendég{b.raw.guestPhone ? ` · ${b.raw.guestPhone}` : ''}</div></div></div>
          ) : people.map((u) => (
            <div key={u} className="flex items-center gap-3 py-1.5">
              <Avatar src={who(u).avatar} name={who(u).name} size={34} />
              <div>
                <div className="text-[13px] font-semibold text-white">{who(u).name}</div>
                <div className="text-[11px] text-[#8a8f99]">CP {fmtInt(who(u).cp)}{u === b.createdBy ? ' · szervező' : ''}</div>
              </div>
            </div>
          ))}
        </div>

        <dl className="mt-4 pt-3 border-t border-[#31383D] text-[13px]">
          {[
            ['Játékmód', b.gameMode || '—'],
            ['Ár', price ? `${fmtInt(price)} Ft${people.length > 1 && !b.guestName ? ` (${fmtInt(Math.round(price / people.length))} Ft / fő)` : ''}` : '—'],
            ['Fizetés', b.raw.onlinePaid ? 'Online' : b.raw.paidOnSite ? 'Helyszínen' : 'Helyszínen, a játék után'],
            ['Lemondási szabály', POLICY[String(club.raw.cancelPolicy ?? '')] ?? 'Közepes'],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3 py-2.5 border-b border-[#31383D] last:border-0">
              <dt className="text-[#8a8f99]">{k}</dt><dd className="text-white text-right">{v}</dd>
            </div>
          ))}
        </dl>

        {b.state !== 'cancelled' && (
          <div className="mt-5 flex flex-col gap-2.5">
            {!b.arrived && (
              <button type="button" onClick={() => upd({ arrived: true, arrivedAt: serverTimestamp() }, 'Megérkezés rögzítve.')}
                className="h-[40px] rounded-full bg-[#2bc48a] text-white text-[14px] font-semibold inline-flex items-center justify-center gap-2 hover:brightness-110">
                <Icon name="Check" size={16} />Megérkeztek
              </button>
            )}
            <Button kind="ghost" icon="ArrowsOutCardinal" onClick={() => setMoving(true)}>Áthelyezés</Button>
            <button type="button" onClick={() => setCancel(true)}
              className="h-[40px] rounded-full border border-[#ef4444] text-[#ef4444] text-[14px] font-semibold hover:bg-[#ef4444]/10">Foglalás lemondása</button>
          </div>
        )}
      </aside>

      {moving && (
        <MoveModal b={b} boards={boards} onClose={() => setMoving(false)} onMove={async (order) => {
          await upd({ board: String(order) }, `Áthelyezve a(z) ${order}. táblára.`);
          setMoving(false);
        }} />
      )}
      {cancel && (
        <ConfirmModal title="Foglalás lemondása" danger confirmLabel="Lemondás" onClose={() => setCancel(false)}
          text="A foglalás lemondott állapotba kerül, a tábla felszabadul. A résztvevőket a mobilapp értesíti."
          onConfirm={async () => { await upd({ state: 'cancelled', cancelledBy: 'club', cancelledAt: serverTimestamp() }, 'Foglalás lemondva.'); setCancel(false); onClose(); }} />
      )}
    </>
  );
}

function MoveModal({ b, boards, onClose, onMove }: { b: ClubBooking; boards: ClubBoard[]; onClose: () => void; onMove: (order: number) => void }) {
  const opts = boards.filter((x) => x.order !== b.board && x.state === 'free');
  const [sel, setSel] = useState(String(opts[0]?.order ?? ''));
  return (
    <Modal title="Áthelyezés másik táblára" onClose={onClose} width={420}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button disabled={!sel} onClick={() => onMove(Number(sel))}>Áthelyezés</Button></>}>
      {opts.length ? (
        <Field label="Új tábla"><Select value={sel} onChange={setSel} options={opts.map((x) => ({ value: String(x.order), label: x.name }))} /></Field>
      ) : <p className="text-[13px] text-[#8a8f99]">Nincs másik foglalható tábla.</p>}
    </Modal>
  );
}

/* ---------- Új foglalás rögzítése (klub-oldali, kézi) ---------- */
export function NewBookingModal({ club, boards, bookings, onClose, initialBoard }: {
  club: ClubInfo; boards: ClubBoard[]; bookings: ClubBooking[]; onClose: () => void; initialBoard?: number;
}) {
  const toast = useToast();
  const { user } = useAuth();
  const today = new Date();
  const [board, setBoard] = useState(String(initialBoard ?? boards.find((x) => x.state === 'free')?.order ?? 1));
  const [date, setDate] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);
  const [time, setTime] = useState(() => {
    const w = openWindow(club.hours, today);
    const base = w && w.from > today ? w.from : new Date(Math.ceil(today.getTime() / 18e5) * 18e5);
    return hhmm(base);
  });
  const [dur, setDur] = useState('60');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [paid, setPaid] = useState(false);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const start = useMemo(() => new Date(`${date}T${time}:00`), [date, time]);
  const price = useMemo(() => {
    const bd = boards.find((x) => x.order === Number(board));
    return bd ? Math.round((bd.priceBase / Math.max(1, bd.priceMinutes)) * Number(dur)) : 0;
  }, [boards, board, dur]);

  const save = async () => {
    setMsg('');
    if (!name.trim()) return setMsg('Add meg a vendég nevét.');
    if (isNaN(start.getTime())) return setMsg('Adj meg érvényes időpontot.');
    const end = start.getTime() + Number(dur) * 60000;
    const clash = bookings.some((x) => x.state !== 'cancelled' && x.board === Number(board) && x.start.getTime() < end && start.getTime() < x.end.getTime());
    if (clash) return setMsg('Erre az időre a tábla már foglalt.');
    setBusy(true);
    try {
      await addDoc(collection(db, 'bookings'), {
        clubId: club.id, clubName: club.name, board: String(board), startAt: Timestamp.fromDate(start), durationMin: Number(dur),
        createdBy: user?.uid ?? '', participantIds: user ? [user.uid] : [], guestName: name.trim(), guestPhone: phone.trim() || null,
        manual: true, onlinePaid: false, paidOnSite: paid, totalPrice: price, state: 'confirmed', visibility: 'private', createdAt: serverTimestamp(),
      });
      toast('Foglalás rögzítve.');
      onClose();
    } catch {
      setMsg('A mentés nem sikerült.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Új foglalás rögzítése" onClose={onClose} width={520}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button icon="Plus" disabled={busy} onClick={save}>Rögzítés</Button></>}>
      <div className="grid grid-cols-2 gap-x-3 gap-y-4">
        <Field label="Tábla"><Select value={board} onChange={setBoard} options={boards.map((x) => ({ value: String(x.order), label: x.name }))} /></Field>
        <Field label="Időtartam"><Select value={dur} onChange={setDur} options={['30', '60', '90', '120', '180'].map((d) => ({ value: d, label: `${d} perc` }))} /></Field>
        <Field label="Dátum"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="[color-scheme:dark]" /></Field>
        <Field label="Kezdés"><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="[color-scheme:dark]" /></Field>
        <Field label="Vendég neve"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="pl. Kiss János" /></Field>
        <Field label="Telefon (opcionális)"><Input icon="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+36 ..." /></Field>
      </div>
      <div className="mt-4 flex items-center justify-between px-4 h-[52px] rounded-xl bg-[#1d232a]">
        <span className="text-[14px] font-semibold text-white">Helyszínen kifizetve</span>
        <Toggle checked={paid} onChange={setPaid} />
      </div>
      <div className="mt-3 text-[13px] text-[#B8B8C3]">Ár: <b className="text-white">{fmtInt(price)} Ft</b></div>
      {msg && <div className="mt-3 text-[13px] text-[#ef4444]" role="alert">{msg}</div>}
    </Modal>
  );
}
